import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import Table from '../common/Table';
import RTSpinner from '../common/RTSpinner';
import AddPayment from './AddPayment';
import SendReminderModal from './SendReminderModal';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastSuccess, toastError, toastConfirm, toastWarning } from '../common/toast';
import '../common/rtCommon.css';
import './ViewUnpaidInvoices.css';

const ViewUnpaidInvoices = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [checkingPaymentStatus, setCheckingPaymentStatus] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [filters, setFilters] = useState({ 
    month: undefined, 
    year: undefined,
    showOverdueOnly: false,
    pgName: '',
    mobile: '',
    paymentLinkStatus: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({ 
    month: undefined, 
    year: undefined,
    showOverdueOnly: false,
    pgName: '',
    mobile: '',
    paymentLinkStatus: ''
  });

  useEffect(() => {
    // Load invoices for current month/year on component mount
    loadUnpaidInvoices(appliedFilters);
  }, []);

  const loadUnpaidInvoices = async (filterParams = appliedFilters, checkPaymentStatus = false) => {
    try {
      if (checkPaymentStatus) {
        setCheckingPaymentStatus(true);
      } else {
        setLoading(true);
      }
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('limit', '1000');
      
      // Add month/year filters if provided
      if (filterParams.month !== undefined && filterParams.year !== undefined) {
        // Both month and year selected: filter to specific month
        const startDate = `${filterParams.year}-${String(filterParams.month).padStart(2, '0')}-01`;
        const lastDay = new Date(filterParams.year, filterParams.month, 0).getDate();
        const endDate = `${filterParams.year}-${String(filterParams.month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      } else if (filterParams.year !== undefined) {
        // Only year selected: filter to entire year
        const startDate = `${filterParams.year}-01-01`;
        const endDate = `${filterParams.year}-12-31`;
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      } else if (filterParams.month !== undefined) {
        // Only month selected: use current year with selected month
        const currentYear = new Date().getFullYear();
        const startDate = `${currentYear}-${String(filterParams.month).padStart(2, '0')}-01`;
        const lastDay = new Date(currentYear, filterParams.month, 0).getDate();
        const endDate = `${currentYear}-${String(filterParams.month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      // If neither month nor year selected, no date filter is applied (loads all)

      // Add PG name filter
      if (filterParams.pgName && filterParams.pgName.trim()) {
        params.append('pg_name', filterParams.pgName.trim());
      }

      // Add mobile filter
      if (filterParams.mobile && filterParams.mobile.trim()) {
        params.append('mobile', filterParams.mobile.trim());
      }

      // Add payment link status filter
      if (filterParams.paymentLinkStatus && filterParams.paymentLinkStatus.trim()) {
        params.append('payment_link_status', filterParams.paymentLinkStatus.trim());
      }

      let allUnpaidInvoices = [];
      
      if (filterParams.showOverdueOnly) {
        // Load only overdue invoices
        params.append('status', 'overdue');
      } else {
        // Load sent, partially paid, and overdue invoices with comma-separated status
        params.append('status', 'sent,partially_paid,overdue');
      }
      
      // Add check_payment_status flag if requested
      if (checkPaymentStatus) {
        params.append('check_payment_status', 'true');
      }
      
      // Single API call with comma-separated status values
      const response = await apiGet(`/invoicing/invoices?${params.toString()}`, {
        timeout: checkPaymentStatus ? 300000 : 60000 // 5 minutes when checking payment status, 1 minute otherwise
      });
      // Handle new response structure {invoices: [...], totalInvoicesAmount: number}
      const responseData = response?.data || [];
      allUnpaidInvoices = Array.isArray(responseData) ? responseData : (responseData.invoices || []);
      
      // Sort by invoice date (newest first)
      allUnpaidInvoices.sort((a, b) => new Date(b.invoice_date) - new Date(a.invoice_date));
      setInvoices(allUnpaidInvoices);
    } catch (error) {
      console.error('Error loading unpaid invoices:', error);
      toastError('Failed to load unpaid invoices. Please try again.');
    } finally {
      setLoading(false);
      setCheckingPaymentStatus(false);
    }
  };

  const handleFilterApply = (appliedFilters) => {
    setFilters(appliedFilters);
    setAppliedFilters(appliedFilters);
    loadUnpaidInvoices(appliedFilters);
  };

  // Generate dynamic title based on actually applied filters
  const getTableTitle = () => {
    let title = "Unpaid Invoices";
    
    if (appliedFilters.showOverdueOnly) {
      title += " - Overdue Only";
    }
    
    if (appliedFilters.month && appliedFilters.year) {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      title += ` (${monthNames[appliedFilters.month - 1]} ${appliedFilters.year})`;
    } else if (appliedFilters.year) {
      title += ` (${appliedFilters.year})`;
    } else if (appliedFilters.month) {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      title += ` (${monthNames[appliedFilters.month - 1]})`;
    } else {
      title += ` (All)`;
    }
    
    // Calculate total pending amount
    const totalPending = invoices.reduce((sum, invoice) => sum + (invoice.amount_remaining || 0), 0);
    
    title += ` - ${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}`;
    title += ` - Total Pending: ₹${totalPending.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    
    if (selectedInvoices.length > 0) {
      title += ` - ${selectedInvoices.length} selected`;
    }
    
    return title;
  };

  const handleSelectInvoice = (invoiceId, checked) => {
    if (checked) {
      setSelectedInvoices(prev => [...prev, invoiceId]);
    } else {
      setSelectedInvoices(prev => prev.filter(id => id !== invoiceId));
      setSelectAll(false);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedInvoices(invoices.map(invoice => invoice.invoice_id));
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleViewInvoice = (invoiceId) => {
    navigate(`/payments/invoices/${invoiceId}/details`);
  };

  const handleAddPayment = async (invoiceId) => {
    try {
      // Fetch invoice details to get PG information
      const invoiceResponse = await apiGet(`/invoicing/invoices/${invoiceId}`);
      if (invoiceResponse?.data) {
        const invoice = invoiceResponse.data;
        
        // Fetch PG details
        const pgResponse = await apiGet(`/pgdetails/${invoice.pg_id}`);
        if (pgResponse?.data) {
          setSelectedInvoiceForPayment({
            invoice: invoice,
            pg: pgResponse.data
          });
          setShowAddPayment(true);
        } else {
          toastError('Failed to load paying guest details');
        }
      } else {
        toastError('Failed to load invoice details');
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
      toastError('Failed to load invoice data');
    }
  };

  const handleCloseAddPayment = () => {
    setShowAddPayment(false);
    setSelectedInvoiceForPayment(null);
    // Reload invoices to reflect any payments made
    loadUnpaidInvoices(appliedFilters);
  };

  const handleGeneratePaymentLink = async (invoiceId) => {
    try {
      // Show confirmation toast
      const confirmed = await toastConfirm(
        true,
        'Are you sure you want to generate a payment link for this invoice? A WhatsApp notification will be sent to the customer.'
      );

      if (!confirmed) {
        return;
      }

      setProcessing(true);
      const response = await apiPost(`/invoicing/invoices/${invoiceId}/generate-payment-link`, {});
      
      if (response?.data?.success) {
        // Check if WhatsApp was sent successfully
        if (response.data.whatsapp_sent) {
          toastSuccess('Payment link generated and sent via WhatsApp');
        } else if (response.data.whatsapp_error) {
          toastWarning(`Payment link generated but WhatsApp failed: ${response.data.whatsapp_error}`);
        } else {
          toastSuccess('Payment link generated successfully');
        }
        loadUnpaidInvoices(appliedFilters); // Reload to get updated status
      } else {
        toastError(response?.data?.error || 'Failed to generate payment link');
      }
    } catch (error) {
      console.error('Error generating payment link:', error);
      const errorMsg = error?.response?.data?.detail || 'Failed to generate payment link. Please try again.';
      toastError(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelPaymentLink = async (invoiceId) => {
    // Show confirmation toast
    const confirmed = await toastConfirm(
      true,
      'Are you sure you want to cancel this payment link?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setProcessing(true);
      const response = await apiPost(`/invoicing/invoices/${invoiceId}/cancel-payment-link`, {});
      
      // apiPost returns axios response, so we need to access response.data
      const data = response.data;
      console.log('Cancel payment link response:', data);
      
      // Update local state immediately to reflect the change
      setInvoices(prevInvoices => 
        prevInvoices.map(inv => 
          inv.invoice_id === invoiceId 
            ? { ...inv, payment_provider_link_status: 'Cancelled' }
            : inv
        )
      );

      toastSuccess(data?.message || 'Payment link cancelled successfully');
    } catch (error) {
      console.error('Error cancelling payment link:', error);
      // Error message from backend or generic message
      const errorMsg = error?.response?.data?.detail || error?.message || 'Failed to cancel payment link. Please try again.';
      toastError(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleSendReminders = async () => {
    try {
      setShowReminderModal(false);
      setProcessing(true);

      const response = await apiPost('/invoicing/send-reminders', {
        invoice_ids: selectedInvoices
      });

      const data = response.data;
      
      if (data.success) {
        const { sent_count, skipped_count, failed_count } = data;
        let message = `Reminders processed: ${sent_count} sent`;
        
        if (skipped_count > 0) {
          message += `, ${skipped_count} skipped (threshold not met)`;
        }
        if (failed_count > 0) {
          message += `, ${failed_count} failed`;
        }
        
        toastSuccess(message);
        
        // Clear selection only, no need to reload as data hasn't changed
        setSelectedInvoices([]);
        setSelectAll(false);
      } else {
        toastError(data.error || 'Failed to send reminders');
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      const errorMsg = error?.response?.data?.detail || 'Failed to send reminders. Please try again.';
      toastError(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  // If AddPayment is shown, render only the AddPayment component
  if (showAddPayment && selectedInvoiceForPayment) {
    return (
      <AddPayment
        onClose={handleCloseAddPayment}
        preSelectedPG={selectedInvoiceForPayment.pg}
        preSelectedInvoiceId={selectedInvoiceForPayment.invoice.invoice_id}
      />
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Generate month options
  const getMonthOptions = () => {
    const months = [
      { value: '', label: 'All Months' },
      { value: 1, label: 'January' },
      { value: 2, label: 'February' },
      { value: 3, label: 'March' },
      { value: 4, label: 'April' },
      { value: 5, label: 'May' },
      { value: 6, label: 'June' },
      { value: 7, label: 'July' },
      { value: 8, label: 'August' },
      { value: 9, label: 'September' },
      { value: 10, label: 'October' },
      { value: 11, label: 'November' },
      { value: 12, label: 'December' }
    ];
    return months;
  };

  // Generate year options (current year and previous 2 years)
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [{ value: '', label: 'All Years' }];
    for (let i = 0; i < 3; i++) {
      const year = currentYear - i;
      years.push({ value: year, label: year.toString() });
    }
    return years;
  };

  const handleMonthChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, month: value === '' ? undefined : parseInt(value) }));
  };

  const handleYearChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, year: value === '' ? undefined : parseInt(value) }));
  };

  const handlePGClick = (pgId) => {
    window.open(`/paying-guest/view/${pgId}`, '_blank');
  };

  const handleBuildingClick = (buildingId) => {
    window.open(`/building/view/${buildingId}`, '_blank');
  };

  const prepareTableData = () => {
    return invoices.map(invoice => {
      return {
        id: invoice.invoice_id,
        invoice_id: invoice.invoice_id,
        invoice_number: invoice.invoice_number,
        pg_id: invoice.pg_id || invoice.paying_guest?.pg_id,
        paying_guest: invoice.paying_guest,
        building_id: invoice.building_id || invoice.building?.id,
        building: invoice.building,
        items: invoice.items_text || 'No items',
        invoice_date: invoice.invoice_date,
        total_amount: invoice.total_amount,
        amount_paid: invoice.amount_paid,
        amount_remaining: invoice.amount_remaining,
        status: invoice.status,
        invoice: invoice
      };
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      sent: 'bg-blue-100 text-blue-800',
      partially_paid: 'bg-yellow-100 text-yellow-800',
      overdue: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1).replace('_', ' ')}
      </span>
    );
  };

  const columns = [
    {
      Header: () => (
        <input
          type="checkbox"
          checked={selectAll}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="rounded border-gray-300"
        />
      ),
      accessor: 'select',
      disableExport: true,
      Cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedInvoices.includes(row.invoice_id)}
          onChange={(e) => handleSelectInvoice(row.invoice_id, e.target.checked)}
          className="rounded border-gray-300"
        />
      ),
      disableSortBy: true,
      width: 50
    },
    {
      Header: 'Invoice Number',
      accessor: 'invoice_number'
    },
    {
      Header: 'Paying Guest',
      accessor: 'paying_guest',
      Cell: ({ row }) => (
        row.pg_id ? (
          <RTButton
            type="link"
            onClick={() => handlePGClick(row.pg_id)}
            className="rt-link-button"
          >
            {row.paying_guest?.name || 'N/A'}
          </RTButton>
        ) : (row.paying_guest?.name || 'N/A')
      ),
      exportValue: (row) => row.paying_guest?.name || 'N/A',
    },
    {
      Header: 'Mobile',
      accessor: 'mobile',
      Cell: ({ row }) => row.paying_guest?.mobile || 'N/A',
      exportValue: (row) => row.paying_guest?.mobile || 'N/A',
    },
    {
      Header: 'Building',
      accessor: 'building',
      Cell: ({ row }) => (
        row.building_id ? (
          <RTButton
            type="link"
            onClick={() => handleBuildingClick(row.building_id)}
            className="rt-link-button"
          >
            {row.building?.name || 'N/A'}
          </RTButton>
        ) : (row.building?.name || 'N/A')
      ),
      exportValue: (row) => row.building?.name || 'N/A',
    },
    {
      Header: 'Invoice Items',
      accessor: 'items'
    },
    {
      Header: 'Total Amount',
      accessor: 'total_amount',
      Cell: ({ value }) => formatAmount(value)
    },
    {
      Header: 'Amount Remaining',
      accessor: 'amount_remaining',
      Cell: ({ value }) => formatAmount(value || 0)
    },
    {
      Header: 'Payment Link',
      accessor: 'payment_link_status',
      Cell: ({ row }) => {
        const status = row.invoice?.payment_provider_link_status || 'Not Generated';
        
        // Map formatted statuses to badge colors
        // Status is now standardized to 'Active' for all newly generated links
        // Legacy values ('Generated', 'Created') are supported for backward compatibility
        const statusColors = {
          'Active': 'bg-green-100 text-green-800',
          'Generated': 'bg-green-100 text-green-800',  // Legacy status - treated as Active
          'Created': 'bg-green-100 text-green-800',    // Legacy status - treated as Active
          'Expired': 'bg-gray-100 text-gray-800',
          'Cancelled': 'bg-red-100 text-red-800',
          'Failed': 'bg-red-100 text-red-800',  // PhonePe payment failed status
          'Not Generated': 'bg-blue-100 text-blue-800',
          'Completed': 'bg-purple-100 text-purple-800',
          'Paid': 'bg-purple-100 text-purple-800',  // Razorpay payment completed status
        };
        
        const colorClass = statusColors[status] || 'bg-gray-100 text-gray-800';
        
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
            {status}
          </span>
        );
      }
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => getStatusBadge(value)
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => {
        const linkStatus = row.invoice?.payment_provider_link_status || 'Not Generated';
        // Determine which buttons to show based on formatted status
        // Status is now standardized to 'Active' for all newly generated links
        // Legacy values ('Generated', 'Created') are supported for backward compatibility
        const showGenerate = ['Expired', 'Not Generated', 'Cancelled', 'Failed'].includes(linkStatus);
        const showCancel = ['Active', 'Generated', 'Created'].includes(linkStatus);
        
        return (
          <div className="flex gap-2 flex-wrap">
            <RTButton
              type="link"
              variant="primary"
              onClick={() => handleAddPayment(row.invoice_id)}
            >
              Add Payment
            </RTButton>
            {showGenerate && (
              <RTButton
                type="link"
                variant="primary"
                onClick={() => handleGeneratePaymentLink(row.invoice_id)}
              >
                Generate Payment Link
              </RTButton>
            )}
            {showCancel && (
              <RTButton
                type="link"
                variant="primary"
                onClick={() => handleCancelPaymentLink(row.invoice_id)}
              >
                Cancel Payment Link
              </RTButton>
            )}
            <RTButton
              type="link"
              variant="primary"
              onClick={() => handleViewInvoice(row.invoice_id)}
            >
              View
            </RTButton>
          </div>
        );
      },
      disableSortBy: true,
      disableExport: true,
    }
  ];

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">View Unpaid Invoices</h2>
          <div className="rt-actions">
            <RTButton variant="secondary" onClick={() => navigate('/payments')}>
              &#8592; Back
            </RTButton>
            {selectedInvoices.length > 0 && (
              <RTButton 
                variant="warning" 
                onClick={() => setShowReminderModal(true)}
              >
                <i className="bi bi-bell me-2"></i>
                Send Reminder ({selectedInvoices.length})
              </RTButton>
            )}
            <RTButton 
              variant="primary" 
              onClick={() => loadUnpaidInvoices(appliedFilters, true)}
              disabled={loading || checkingPaymentStatus}
            >
              {checkingPaymentStatus ? 'Checking...' : 'Check Payment Status'}
            </RTButton>
          </div>
        </div>

        {/* Payment status check info banner */}
        {checkingPaymentStatus && (
            <RTSpinner fullScreen={false} message='Checking payment link status with payment provider... This may take a few minutes depending on the number of invoices. Please wait.' />
        )}

        <div className="rt-main-section">
          {/* Custom Filter Section */}
          <div className="invoice-filter-container">
            <div className="invoice-filter-content">
              <div className="invoice-filter-row">
                <div className="invoice-filter-group-compact">
                  <label className="invoice-filter-label">Month</label>
                  <select
                    className="invoice-filter-select"
                    value={filters.month || ''}
                    onChange={handleMonthChange}
                  >
                    {getMonthOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="invoice-filter-group-compact">
                  <label className="invoice-filter-label">Year</label>
                  <select
                    className="invoice-filter-select"
                    value={filters.year || ''}
                    onChange={handleYearChange}
                  >
                    {getYearOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="invoice-filter-group-medium">
                  <label className="invoice-filter-label">PG Name</label>
                  <input
                    type="text"
                    className="invoice-filter-input"
                    placeholder="Search by name..."
                    value={filters.pgName}
                    onChange={(e) => setFilters(prev => ({ ...prev, pgName: e.target.value }))}
                  />
                </div>

                <div className="invoice-filter-group-medium">
                  <label className="invoice-filter-label">Mobile</label>
                  <input
                    type="text"
                    className="invoice-filter-input"
                    placeholder="Search mobile..."
                    value={filters.mobile}
                    onChange={(e) => setFilters(prev => ({ ...prev, mobile: e.target.value }))}
                  />
                </div>

                <div className="invoice-filter-group-compact">
                  <label className="invoice-filter-label">Payment Link</label>
                  <select
                    className="invoice-filter-select"
                    value={filters.paymentLinkStatus}
                    onChange={(e) => setFilters(prev => ({ ...prev, paymentLinkStatus: e.target.value }))}
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="expired">Expired</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="completed">Completed</option>
                    <option value="not_generated">Not Generated</option>
                  </select>
                </div>
              </div>
              
              <div className="invoice-filter-row">
                <div className="invoice-filter-checkbox-group">
                  <label className="invoice-filter-checkbox-label">
                    <input
                      type="checkbox"
                      checked={filters.showOverdueOnly}
                      onChange={(e) => setFilters(prev => ({ ...prev, showOverdueOnly: e.target.checked }))}
                      className="invoice-filter-checkbox"
                    />
                    <span className="invoice-filter-checkbox-text">Show overdue only</span>
                  </label>
                </div>
                <div className="invoice-filter-actions">
                  <RTButton
                    variant="secondary"
                    onClick={() => {
                      const defaultFilters = { 
                        month: undefined, 
                        year: undefined,
                        showOverdueOnly: false,
                        pgName: '',
                        mobile: '',
                        paymentLinkStatus: ''
                      };
                      setFilters(defaultFilters);
                      setAppliedFilters(defaultFilters);
                      loadUnpaidInvoices(defaultFilters);
                    }}
                    className="invoice-filter-clear-btn"
                  >
                    Clear Filters
                  </RTButton>
                  <RTButton
                    variant="primary"
                    onClick={() => handleFilterApply(filters)}
                    className="invoice-filter-apply-btn"
                  >
                    Apply Filters
                  </RTButton>
                </div>
              </div>
            </div>
          </div>

          {/* Invoices Table */}
          <div className="invoices-table-container">
            {loading ? (
              <div className="loading-container">
                <RTSpinner fullScreen={false} />
                <p>Loading unpaid invoices...</p>
              </div>
            ) : (
              <Table
                columns={columns}
                data={prepareTableData()}
                title={getTableTitle()}
                noDataText="No unpaid invoices found."
                exportable={true}
                exportFileName="unpaid_invoices"
                searchable={true}
                sortable={true}
                pagination={true}
                pageSize={25}
              />
            )}
          </div>
        </div>

        {/* Loading Spinner */}
        {processing && <RTSpinner fullScreen={true} />}

        {/* Send Reminder Modal */}
        <SendReminderModal
          show={showReminderModal}
          onHide={() => setShowReminderModal(false)}
          onConfirm={handleSendReminders}
          selectedCount={selectedInvoices.length}
        />
      </div>
    </div>
  );
};

export default ViewUnpaidInvoices;
